import { alpha, type Theme } from "@mui/material/styles";
import type { AppWidgetsComponentStyles } from "../types";

export function buildWidgetsComponentStyles(_theme: Theme): AppWidgetsComponentStyles {
  return {
  advancedCodeToolbar: {
    minHeight: 40,
    mb: 1.5,
    borderBottom: 1,
    borderColor: "divider",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 1,
    flexShrink: 0,
  },
  advancedCodeTabs: {
    minHeight: 40,
    mb: 1.5,
    width: "fit-content",
    maxWidth: "100%",
  },
    advancedCodeFullscreenButton: {
      border: "1px solid",
      borderColor: "divider",
      borderRadius: 1,
    },
    advancedCodeFullscreenIcon: { fontSize: 18 },
    advancedCodeDialogContent: { display: "flex", flexDirection: "column", minHeight: 0, pt: 0 },
    advancedCodeEditorBox: { flex: 1, minHeight: 0 },
    studioRoot: {
      display: "flex",
      flexDirection: "column",
      gap: 1.5,
      minHeight: { xs: "auto", lg: "calc(100vh - 88px)" },
      mx: 0,
      minWidth: 0,
    },
    studioStickyHeader: (theme) => ({
      position: "sticky",
      top: 72,
      zIndex: 20,
      borderRadius: 1.25,
      border: "1px solid",
      borderColor: "divider",
      bgcolor: alpha(theme.palette.background.paper, 0.92),
      backdropFilter: "blur(10px)",
      mb: 0,
      flexShrink: 0,
    }),
    studioHeaderInner: { px: { xs: 1.25, md: 1.75 }, py: 1 },
    studioHeaderTitleRow: { minWidth: 0, flex: 1 },
    studioBackButton: { border: 1, borderColor: "divider" },
    studioTitleCaption: { display: "block", lineHeight: 1.2 },
    studioWidgetTitle: { maxWidth: { xs: 220, md: 420 } },
    studioFormatChip: { height: 22, fontSize: 11 },
    studioErrorAlert: { mb: 0, py: 0.5, flexShrink: 0 },
    studioBodyRow: {
      display: "flex",
      flexDirection: { xs: "column", lg: "row" },
      gap: 1.25,
      flex: 1,
      minHeight: 0,
    },
    studioNavPaper: {
      width: { xs: "100%", lg: 200 },
      flexShrink: 0,
      border: "1px solid",
      borderColor: "divider",
      borderRadius: 1.25,
      overflow: "hidden",
      bgcolor: "background.paper",
    },
    studioNavHeader: { px: 1.25, py: 1, borderBottom: 1, borderColor: "divider" },
    studioNavList: { py: 0.5 },
    studioNavItem: (selected) => (theme) => ({
      mx: 0.75,
      my: 0.2,
      borderRadius: 1,
      minHeight: 36,
      fontWeight: selected ? 600 : 500,
      color: selected ? theme.palette.primary.contrastText : theme.palette.text.primary,
      "&:hover": {
        bgcolor: selected ? theme.palette.primary.dark : theme.palette.action.hover,
      },
      "&.Mui-selected": {
        bgcolor: theme.palette.primary.main,
        color: theme.palette.primary.contrastText,
        "&:hover": {
          bgcolor: theme.palette.primary.dark,
        },
      },
    }),
    studioNavIcon: (selected) => (theme: Theme) => ({
      minWidth: 32,
      color: selected ? theme.palette.primary.contrastText : theme.palette.text.primary,
      opacity: selected ? 1 : 0.85,
    }),
    studioContent: (isPreviewSection) => ({
      flex: 1,
      minWidth: 0,
      minHeight: isPreviewSection ? { xs: 480, lg: 0 } : { xs: "auto", lg: 0 },
      display: isPreviewSection ? "flex" : "block",
      flexDirection: isPreviewSection ? "column" : undefined,
      overflow: isPreviewSection ? "hidden" : { xs: "visible", lg: "auto" },
      pr: { lg: 0.5 },
    }),
    docsTabs: {
      minHeight: 40,
      mb: 2,
      width: "fit-content",
      maxWidth: "100%",
    },
    docsCode: (codeTheme) => ({
      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", monospace',
      fontSize: 12,
      lineHeight: 1.55,
      p: 1.5,
      m: 0,
      borderRadius: 1,
      bgcolor: alpha(codeTheme.palette.text.primary, 0.04),
      border: "1px solid",
      borderColor: "divider",
      overflowX: "auto",
      whiteSpace: "pre",
    }),
    docsCaption: { display: "block", mt: 0.75, mb: 2 },
    docsInlineCode: (codeTheme) => ({
      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
      fontSize: "0.85em",
      px: 0.6,
      py: 0.15,
      borderRadius: 0.5,
      bgcolor: alpha(codeTheme.palette.text.primary, 0.06),
      border: "1px solid",
      borderColor: "divider",
    }),
    docsSectionTitle: { mt: 2.5, mb: 0.75 },
    docsList: { m: 0, pl: 2.5 },
    docsListItem: { mb: 0.6 },
    docsAlert: { my: 1.5 },
    docsPlaceholderRow: { mb: 0.5 },
    docsPlaceholderIntro: { mb: 1 },
  };
}
